/**
 * The following things will need to be fixed:
 *  - Async loading of the locales or only include the locale necessary for a bundle
 *  - Grab the user locale from somewhere other than the AJS meta.
 */
import type { Locale } from 'date-fns';
import { format as dateFnsFormat } from 'date-fns';
import cs from 'date-fns/locale/cs';
import da from 'date-fns/locale/da';
import de from 'date-fns/locale/de';
import en from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fi from 'date-fns/locale/fi';
import fr from 'date-fns/locale/fr';
import hu from 'date-fns/locale/hu';
import is from 'date-fns/locale/is';
import it from 'date-fns/locale/it';
import ja from 'date-fns/locale/ja';
import ko from 'date-fns/locale/ko';
import nl from 'date-fns/locale/nl';
import pl from 'date-fns/locale/pl';
import pt from 'date-fns/locale/pt';
import ro from 'date-fns/locale/ro';
import ru from 'date-fns/locale/ru';
import sk from 'date-fns/locale/sk';
import sv from 'date-fns/locale/sv';
import { getMetaData } from '@helpcenter/meta';

const supportedLocales = [
    'cs',
    'da',
    'de',
    'en',
    'es',
    'fi',
    'fr',
    'hu',
    'is',
    'it',
    'ja',
    'ko',
    'nl',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sv',
];
type LocaleKey = (typeof supportedLocales)[number];

const supportedLocale: Record<LocaleKey, Locale> = {
    cs,
    da,
    de,
    en,
    es,
    fi,
    fr,
    hu,
    is,
    it,
    ja,
    ko,
    nl,
    pl,
    pt,
    ro,
    ru,
    sk,
    sv,
};

const isValidLocale = (str: string): str is LocaleKey => {
    return supportedLocales.includes(str);
};

export const format: (date: Date | number, dateTimeFormat: string) => string = (date, dateTimeFormat) => {
    const localeKeys = (getMetaData()?.userLocale || '').replace('_', '-').split('-');
    if (localeKeys && localeKeys.length > 0 && isValidLocale(localeKeys[0])) {
        const localeToUse = supportedLocale[localeKeys[0]];
        return dateFnsFormat(date, dateTimeFormat, {
            locale: localeToUse,
        });
    }

    return dateFnsFormat(date, dateTimeFormat);
};

type DayRange = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const isValidWeekRange = (firstDay: number | undefined): firstDay is DayRange => {
    return (firstDay as DayRange) >= 0 && (firstDay as DayRange) <= 6;
};

export const getDayOfTheWeek = (firstDay: number | undefined): 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined => {
    if (isValidWeekRange(firstDay)) return firstDay;
    return undefined;
};

export const getWeekStartDay = (firstDay?: number, useISO8601WeekNumbers?: boolean) => {
    return useISO8601WeekNumbers ? 1 : getDayOfTheWeek(firstDay);
};
